import { useCallback, useState } from 'react';
import { useSuppliers } from '../../hooks/useSuppliers';
import { toast } from 'sonner';
import { useSubmitSetupApproval } from '@/features/production/hooks/useSetupApprovals';
import { SETUP_APPROVAL_TYPE } from '@/features/production/services/setupApprovalApi';
import { useConfirm } from '@/shared/contexts/confirm-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { useActiveInputTypes } from "../../hooks/useInputTypes";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { PagedCombobox } from '@/components/ui/pagedcombobox';
import { useUnitsOfMeasure } from '@/shared/hooks/useUnitsOfMeasure';
import { useVatRates } from '@/shared/hooks/useMetaData';
import { InputTypeCombobox } from './InputTypeComboBox';
import { cropTypesApi, type CropType } from '@/features/production/services/cropTypesApi';
import { cropVarietiesApi, type CropVariety } from '@/features/production/services/cropVarietiesApi';
import type { AxiosError } from 'axios';

// Units will be loaded via `useUnitsOfMeasure`

const initialFormData = {
  inputName: '',
  description: '',
  inputTypeId: '',
  supplierId: '',
  supplierProductCode: '',
  unitOfMeasure: '',
  unitCost: 0,
  vatPercentage: '',
  hasExpiryDate: false,
  shelfLifeDays: 0,
  safetyInstructions: '',
  storageRequirements: '',
  // Variety surfaced per spec §b/§f. Required when the chosen InputType is seed-related.
  cropTypeId: '',
  variety: '',
};

export const CreateInputDialog = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const submitApproval = useSubmitSetupApproval();
  const confirm = useConfirm();
  const suppliersQuery = useSuppliers();
  const inputTypesQuery = useActiveInputTypes();
  const unitsQuery = useUnitsOfMeasure();
  const vatRatesQuery = useVatRates();
  const [selectedCropType, setSelectedCropType] = useState<CropType | null>(null);
  const [selectedVariety, setSelectedVariety] = useState<CropVariety | null>(null);

  const fetchCropTypes = useCallback(
    ({ search, pageNumber, pageSize }: { search: string; pageNumber: number; pageSize: number }) =>
      cropTypesApi.getCropTypesPaged({ search, pageNumber, pageSize, isActive: true, sortBy: "name" }),
    [],
  );

  const fetchVarieties = useCallback(
    ({ search, pageNumber, pageSize }: { search: string; pageNumber: number; pageSize: number }) =>
      cropVarietiesApi.getCropVarietiesPaged({
        search,
        pageNumber,
        pageSize,
        isActive: true,
        cropTypeId: selectedCropType?.id,
        sortBy: "name",
      }),
    [selectedCropType?.id],
  );

  // Dedupe units coming from the API by their `value` field (server sometimes returns duplicates)
  const dedupedUnits = unitsQuery.data
    ? Array.from(new Map(unitsQuery.data.map((u) => [u.value, u])).values())
    : [];

  const selectedInputType = inputTypesQuery.data?.find((t) => t.id === formData.inputTypeId);
  const isSeedInput = !!selectedInputType?.name && selectedInputType.name.toLowerCase().includes("seed");

  const handleChange = (
    field: keyof typeof initialFormData,
    value: string | number | boolean
  ) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "inputTypeId") {
        const newType = inputTypesQuery.data?.find((t) => t.id === value);
        const stillSeed = !!newType?.name && newType.name.toLowerCase().includes("seed");
        if (!stillSeed) {
          next.cropTypeId = "";
          next.variety = "";
          setSelectedCropType(null);
          setSelectedVariety(null);
        }
      }
      return next;
    });
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleCropTypeChange = (item: CropType | null) => {
    setSelectedCropType(item);
    setSelectedVariety(null);
    setFormData((prev) => ({ ...prev, cropTypeId: item?.id ?? "", variety: "" }));
    if (fieldErrors.cropTypeId) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.cropTypeId;
        return next;
      });
    }
  };

  const handleVarietyChange = (item: CropVariety | null) => {
    setSelectedVariety(item);
    setFormData((prev) => ({ ...prev, variety: item?.name ?? "" }));
    if (fieldErrors.variety) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.variety;
        return next;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (isSeedInput) {
      const errs: Record<string, string[]> = {};
      if (!formData.cropTypeId) errs.cropTypeId = ["Crop type is required for seed inputs."];
      if (!formData.variety.trim()) errs.variety = ["Variety is required for seed inputs."];
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        return;
      }
    }

    const payload = {
      inputName: formData.inputName,
      description: formData.description || null,
      inputTypeId: formData.inputTypeId,
      supplierId: formData.supplierId || null,
      supplierProductCode: formData.supplierProductCode || null,
      // 0 is the Unspecified sentinel — backend will set the real unit from the
      // first stock receipt. Empty string from the unselected Select coerces to 0.
      unitOfMeasure: formData.unitOfMeasure === '' ? 0 : Number(formData.unitOfMeasure),
      unitCost: formData.unitCost || null,
      vatPercentage: formData.vatPercentage ? Number(formData.vatPercentage) : null,
      hasExpiryDate: Boolean(formData.hasExpiryDate),
      shelfLifeDays: formData.hasExpiryDate ? Number(formData.shelfLifeDays) || null : null,
      safetyInstructions: formData.safetyInstructions || null,
      storageRequirements: formData.storageRequirements || null,
      cropTypeId: formData.cropTypeId || null,
      variety: formData.variety.trim() || null,
    };

    confirm({
      title: "Submit input for approval?",
      description: `“${payload.inputName}” will be submitted for GM approval before it becomes available.`,
      confirmLabel: "Submit",
      onConfirm: () =>
        submitApproval
          .mutateAsync({
            approvalType: SETUP_APPROVAL_TYPE.Input,
            entityJson: JSON.stringify(payload),
            description: `New Input: ${payload.inputName}`,
          })
          .then(() => {
            toast.success('Submitted for GM approval');
            setFormData(initialFormData);
            setSelectedCropType(null);
            setSelectedVariety(null);
            setOpen(false);
          })
          .catch((error: unknown) => {
        const errorData = (error as AxiosError<{
          errors?: string[];
          message?: string;
        }>).response?.data;
        const errorsList = errorData?.errors || [];

        if (Array.isArray(errorsList) && errorsList.length > 0) {
          // Parse field-specific errors from the API response
          const parsedErrors: Record<string, string[]> = {};
          
          errorsList.forEach((errorMsg: string) => {
            const [fieldName, ...messageParts] = errorMsg.split(':');
            const message = messageParts.join(':').trim();
            
            // Map API field names to form field names
            const fieldMapping: Record<string, string> = {
              'Code': 'inputCode',
              'Name': 'inputName',
              'InputCode': 'inputCode',
              'InputName': 'inputName',
              'InputTypeId': 'inputTypeId',
              'SupplierId': 'supplierId',
              'SupplierProductCode': 'supplierProductCode',
              'UnitOfMeasure': 'unitOfMeasure',
              'PackSize': 'packSize',
              'ReorderLevel': 'reorderLevel',
              'MinimumStock': 'minimumStock',
              'MaximumStock': 'maximumStock',
              'UnitCost': 'unitCost',
              'VatPercentage': 'vatPercentage',
              'ShelfLifeDays': 'shelfLifeDays',
              'SafetyInstructions': 'safetyInstructions',
              'StorageRequirements': 'storageRequirements',
              'Description': 'description',
            };

            const mappedFieldName = fieldMapping[fieldName.trim()] || fieldName.trim().toLowerCase();
            
            if (!parsedErrors[mappedFieldName]) {
              parsedErrors[mappedFieldName] = [];
            }
            parsedErrors[mappedFieldName].push(message);
            
            // Show individual toast for each error
            toast.error(`${fieldName.trim()}: ${message}`);
          });
          
          setFieldErrors(parsedErrors);
        } else if (errorData?.message) {
          toast.error(errorData.message);
        } else {
          toast.error(error instanceof Error ? error.message : 'Failed to submit for approval');
        }
        throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          New Input
        </Button>
      </DialogTrigger>
      <DialogContent 
        className="max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 w-[95vw] sm:!max-w-[1400px]">
        <DialogHeader>
          <DialogTitle>Create New Input</DialogTitle>
          <DialogDescription>
            Add a new input/product to inventory
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="inputName">
                  Input Name <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="inputName"
                  value={formData.inputName}
                  onChange={(e) => handleChange('inputName', e.target.value)}
                  required
                  disabled={submitApproval.isPending}
                />
                {fieldErrors.inputName && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.inputName.join(', ')}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="inputTypeId">
                  Input Type <span className="text-red-500">*</span>
                </FieldLabel>
                {inputTypesQuery.isLoading ? (
                  <div className="h-10 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <InputTypeCombobox
                    inputTypes={inputTypesQuery.data || []}
                    isLoading={inputTypesQuery.isLoading}
                    selectedValue={formData.inputTypeId}
                    onSelect={(value) => handleChange("inputTypeId", value)}
                  />
                )}
                {fieldErrors.inputTypeId && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.inputTypeId.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="supplierId">
                  Supplier
                </FieldLabel>
                {suppliersQuery.isLoading ? (
                  <div className="h-10 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <Select
                    value={formData.supplierId}
                    onValueChange={(value) => handleChange('supplierId', value)}
                    disabled={submitApproval.isPending}
                  >
                    <SelectTrigger id="supplierId" className="w-full">
                      <SelectValue placeholder="Select Supplier (Optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliersQuery.data?.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.supplierName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {fieldErrors.supplierId && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.supplierId.join(', ')}
                  </div>
                )}
              </Field>

              <Field className="col-span-2">
                <FieldLabel htmlFor="description">
                  Description
                </FieldLabel>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  disabled={submitApproval.isPending}
                  placeholder="Detailed description of this input (Optional)"
                  className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
                {fieldErrors.description && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.description.join(', ')}
                  </div>
                )}
              </Field>
            </div>
          </div>

          {/* Supplier & Unit Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Supplier & Unit Details</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="supplierProductCode">
                  Supplier Product Code
                </FieldLabel>
                <Input
                  id="supplierProductCode"
                  value={formData.supplierProductCode}
                  onChange={(e) =>
                    handleChange('supplierProductCode', e.target.value)
                  }
                  disabled={submitApproval.isPending}
                  placeholder="Optional"
                />
                {fieldErrors.supplierProductCode && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.supplierProductCode.join(', ')}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="unitOfMeasure">
                  Unit of Measure
                </FieldLabel>
                {unitsQuery.isLoading ? (
                  <div className="h-10 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <Select
                    value={formData.unitOfMeasure}
                    onValueChange={(value) => handleChange('unitOfMeasure', value)}
                    disabled={submitApproval.isPending}
                  >
                    <SelectTrigger id="unitOfMeasure" className="w-full">
                      <SelectValue placeholder="Optional — set on first receipt" />
                    </SelectTrigger>
                    <SelectContent>
                      {dedupedUnits.map((unit) => (
                        <SelectItem key={unit.value} value={unit.value.toString()}>
                          {unit.name} {unit.abbreviation ? `(${unit.abbreviation})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Leave blank if you'll establish the unit when receiving the first stock batch.
                </p>
                {fieldErrors.unitOfMeasure && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.unitOfMeasure.join(', ')}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="unitCost">
                  Unit Cost
                </FieldLabel>
                <Input
                  id="unitCost"
                  type="number"
                  step="0.01"
                  value={formData.unitCost}
                  onChange={(e) =>
                    handleChange('unitCost', parseFloat(e.target.value) || 0)
                  }
                  disabled={submitApproval.isPending}
                  placeholder="Optional"
                />
                {fieldErrors.unitCost && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.unitCost.join(', ')}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="vatPercentage">
                  VAT Percentage (%)
                </FieldLabel>
                {vatRatesQuery.isLoading ? (
                  <div className="h-10 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <Select
                    value={formData.vatPercentage}
                    onValueChange={(value) => handleChange('vatPercentage', value)}
                    disabled={submitApproval.isPending}
                  >
                    <SelectTrigger id="vatPercentage" className="w-full">
                      <SelectValue placeholder="Select VAT Rate" />
                    </SelectTrigger>
                    <SelectContent>
                      {vatRatesQuery.data?.map((rate) => (
                        <SelectItem key={rate.rate} value={rate.rate.toString()}>
                          {rate.rate}% {rate.description ? `- ${rate.description}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {fieldErrors.vatPercentage && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.vatPercentage.join(', ')}
                  </div>
                )}
              </Field>
            </div>
          </div>

          {/* Crop & Variety — surfaced per spec §b/§f. For seed inputs, both Crop
              Type and Variety come from the catalogue (dropdowns) so receivers see
              consistent labels. For non-seed inputs the Variety stays free-text
              for optional varietal labels on fertilisers, growing media, etc. */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Crop & Variety</h3>
            <div className={isSeedInput ? "grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0" : ""}>
              {isSeedInput && (
                <Field>
                  <FieldLabel htmlFor="cropTypeId">
                    Crop Type <span className="text-red-500">*</span>
                  </FieldLabel>
                  <PagedCombobox<CropType>
                    title="Crop Type"
                    value={selectedCropType}
                    onChange={handleCropTypeChange}
                    fetchPage={fetchCropTypes}
                    disabled={submitApproval.isPending}
                  />
                  {fieldErrors.cropTypeId && (
                    <div className="text-red-500 text-xs mt-1">
                      {fieldErrors.cropTypeId.join(', ')}
                    </div>
                  )}
                </Field>
              )}

              <Field>
                <FieldLabel htmlFor="variety">
                  Variety {isSeedInput && <span className="text-red-500">*</span>}
                </FieldLabel>
                {isSeedInput ? (
                  <PagedCombobox<CropVariety>
                    title="Variety"
                    value={selectedVariety}
                    onChange={handleVarietyChange}
                    fetchPage={fetchVarieties}
                    disabled={submitApproval.isPending || !selectedCropType}
                  />
                ) : (
                  <Input
                    id="variety"
                    value={formData.variety}
                    onChange={(e) => handleChange('variety', e.target.value)}
                    disabled={submitApproval.isPending}
                    placeholder="e.g. ANSAL F1"
                  />
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {isSeedInput
                    ? "Receivers see this variety on every receive-stock line."
                    : "Optional varietal label for fertilizers, growing media, etc."}
                </p>
                {fieldErrors.variety && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.variety.join(', ')}
                  </div>
                )}
              </Field>
            </div>
          </div>

          {/* Expiry & Safety */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Expiry & Safety</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasExpiryDate"
                  checked={formData.hasExpiryDate}
                  onCheckedChange={(checked) =>
                    handleChange('hasExpiryDate', checked)
                  }
                  disabled={submitApproval.isPending}
                />
                <Label htmlFor="hasExpiryDate" className="cursor-pointer">
                  Has Expiry Date
                </Label>
              </div>

              {formData.hasExpiryDate && (
                <Field>
                  <FieldLabel htmlFor="shelfLifeDays">
                    Shelf Life (Days) <span className="text-red-500">*</span>
                  </FieldLabel>
                  <Input
                    id="shelfLifeDays"
                    type="number"
                    value={formData.shelfLifeDays}
                    onChange={(e) =>
                      handleChange('shelfLifeDays', parseInt(e.target.value) || 0)
                    }
                    required={formData.hasExpiryDate}
                    disabled={submitApproval.isPending}
                  />
                  {fieldErrors.shelfLifeDays && (
                    <div className="text-red-500 text-xs mt-1">
                      {fieldErrors.shelfLifeDays.join(', ')}
                    </div>
                  )}
                </Field>
              )}

              <Field>
                <FieldLabel htmlFor="safetyInstructions">
                  Safety Instructions
                </FieldLabel>
                <textarea
                  id="safetyInstructions"
                  value={formData.safetyInstructions}
                  onChange={(e) =>
                    handleChange('safetyInstructions', e.target.value)
                  }
                  disabled={submitApproval.isPending}
                  placeholder="Safety precautions and handling instructions"
                  className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
                {fieldErrors.safetyInstructions && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.safetyInstructions.join(', ')}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="storageRequirements">
                  Storage Requirements
                </FieldLabel>
                <textarea
                  id="storageRequirements"
                  value={formData.storageRequirements}
                  onChange={(e) =>
                    handleChange('storageRequirements', e.target.value)
                  }
                  disabled={submitApproval.isPending}
                  placeholder="Temperature, humidity, and other storage conditions"
                  className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
                {fieldErrors.storageRequirements && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.storageRequirements.join(', ')}
                  </div>
                )}
              </Field>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={submitApproval.isPending}
          >
            {submitApproval.isPending ? 'Submitting...' : 'Submit for Approval'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
